import type { Locale } from "#i18n";
import type { Language } from "mws-platform-ts-sdk";
import type { NuxtApp } from "nuxt/app";

import {
  currencyCodeCookieKey,
  i18nRedirectCookieKey,
} from "~/constants/cookieKeys";

export default defineNuxtPlugin({
  dependsOn: ["platform", "segment"],
  setup: (nuxtApp) => {
    findAndSetInitialActiveCurrency();

    const platformStore = usePlatformStore();
    const platformConfiguration = platformStore.tryGetPlatformConfiguration;

    const availableLanguages =
      platformConfiguration?.properties.availableLanguages ?? [];
    const defaultLanguage = platformConfiguration?.properties
      .defaultLanguage ?? {
      isoLanguageCode: "en",
    };

    const { setLocale } = useSetLocale();

    // Only check the browser language and do the redirect when the app has fully loaded and resolved
    nuxtApp.hook("app:suspense:resolve", () => {
      // Safely access the browser language
      const browserLocale =
        navigator && "language" in navigator && navigator.language
          ? navigator.language
          : "en";

      const matchingLocaleCode = findMatchingLocaleCode(browserLocale);

      // Find the initial locale the set based on cookie/browser/platform languages
      const initialLocale = getInitialLocale({
        // `as` needed to let TS know we have things like `$segment` and `$i18n` available
        nuxtApp: nuxtApp as NuxtApp,
        availableLanguages,
        defaultLanguage,
        selectedLanguage: matchingLocaleCode,
      });

      setLocale(initialLocale, { mode: "plugin" });
    });
  },
});

function findAndSetInitialActiveCurrency() {
  const { setActiveCurrency } = useActiveCurrency();

  // if currency was set in the cookies, that will be used automatically
  const storedCurrencyCode = useCookie<string>(currencyCodeCookieKey);
  if (storedCurrencyCode.value) return;

  const currencyCodeForEstimatedCountry = countryToCurrencyCode(
    getEstimatedUserCountry(),
  );

  if (currencyCodeForEstimatedCountry) {
    setActiveCurrency(currencyCodeForEstimatedCountry);
  }
}

const getInitialLocale = (options: {
  nuxtApp: NuxtApp;
  availableLanguages: Language[];
  defaultLanguage: Language;
  selectedLanguage: string;
}): Locale => {
  const { nuxtApp, availableLanguages, defaultLanguage, selectedLanguage } =
    options;

  // Get currently selected language from the cookie
  const cookieLanguage = useCookie<Locale>(i18nRedirectCookieKey, {
    readonly: true,
  });

  const i18nLocale = unref(nuxtApp.$i18n.locale);
  const defaultLocale = defaultLanguage.isoLanguageCode;

  let localeToChoose: Locale | undefined = undefined;
  // Only redirect if the language is the main one, otherwise we just overwrite it
  if (i18nLocale === defaultLocale) {
    // If we have a cookie set, use it to overwrite
    if (cookieLanguage.value && cookieLanguage.value !== defaultLocale) {
      localeToChoose = cookieLanguage.value;
      // If we have a browser language set, use it to overwrite
    } else if (selectedLanguage && selectedLanguage !== defaultLocale) {
      localeToChoose = selectedLanguage as Locale;
    }
  }

  // Default back to i18n selected URL locale
  if (!localeToChoose) {
    localeToChoose = i18nLocale;
  }

  // If language is available for platform, set and use it
  if (
    availableLanguages.some((lang) => lang.isoLanguageCode === localeToChoose)
  ) {
    return localeToChoose;
  }

  const getIsDefaultPlatformLanguageSupported = () => {
    const allDefinedLocales = unref(nuxtApp.$i18n.locales);

    return allDefinedLocales.some((locale) => {
      const localeCode = typeof locale === "string" ? locale : locale.code;

      return localeCode === defaultLocale;
    });
  };

  // Otherwise default back to default locale for platfor
  // FTG doesn't support everything for example
  if (getIsDefaultPlatformLanguageSupported()) {
    return defaultLocale as Locale;
  }

  // Otherwise default back to english
  return "en";
};
